import { useEffect, useRef, useMemo, useCallback } from 'react';
import { X, ChevronLeft, Gift, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ResponsiveWizardShell } from '@/components/shared/ResponsiveWizardShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { DateField, getDobBounds, isWithinDateBounds } from '@/components/shared/DateField';
import { Spinner } from '@/components/ui/spinner';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LemonadeAvatar } from '@/components/auth/LemonadeAvatar';
import { useProfileWizard } from '@/contexts/ProfileWizardContext';
import { useProfileProgress } from '@/hooks/useProfileProgress';
import { fireConfetti } from '@/lib/perf/confetti';
import { getInputHint } from '@/lib/questionHints';
import { getAnswerSchema, requiredStringSchema } from '@/lib/schemas/survey';

interface LemonadeProfileWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LemonadeProfileWizard({ isOpen, onClose }: LemonadeProfileWizardProps) {
  const {
    currentStepIndex,
    currentFieldIndex,
    currentStep,
    currentField,
    selectedValues,
    isSaving,
    isLoading,
    hasError,
    completedSteps,
    progress,
    selectOption,
    setTextValue,
    continueToNext,
    saveCurrentField,
    skipField,
    goBack,
    canContinue,
    canGoBack,
    earnedEntries,
    remainingEntries,
    profileSteps,
    isEditing,
  } = useProfileWizard();

  // Use the SAME canonical completion stat as the My Data card
  // (answered / total active questions) so the wizard header % matches it
  // exactly. The context `progress` (answered / wizard fields) uses a different
  // denominator and drifts from the card; fall back to it only until the query
  // resolves. continueToNext invalidates ['profile-progress'] so this ticks up
  // live as the user answers.
  const { data: profileProgress } = useProfileProgress();
  const completionPercentage = profileProgress?.percentage ?? progress;

  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  const isMulti = currentField?.type === 'multi';
  const isText = currentField?.type === 'text';
  const isDate = currentField?.type === 'date';

  const dateBounds = useMemo(() => getDobBounds(), []);

  const inputHint = useMemo(
    () => isText && currentField ? getInputHint(currentField.question) : null,
    [isText, currentField],
  );

  const textSchema = useMemo(() => {
    if (!isText || !currentField) return z.object({ answer: requiredStringSchema });
    const { schema } = getAnswerSchema(currentField.question);
    return z.object({ answer: schema });
  }, [isText, currentField]);

  const form = useForm<{ answer: string }>({
    resolver: isText ? zodResolver(textSchema) : undefined,
    defaultValues: { answer: '' },
    mode: 'onChange',
    delayError: 300,
  });

  const currentValue = currentField ? selectedValues[currentField.questionMappingId] : undefined;

  useEffect(() => {
    const defaultVal = typeof currentValue === 'string' ? currentValue : '';
    form.reset({ answer: defaultVal });
    // Re-focus text input when navigating between text questions
    if (isText) {
      requestAnimationFrame(() => textInputRef.current?.focus());
    }
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, [currentStepIndex, currentFieldIndex, form]);

  useEffect(() => {
    // Celebrate + auto-close only when the wizard is actually OPEN and complete.
    // REK-1913: the wizard is now always mounted on the My Data tab (so editing
    // works at 100% complete), so without the `isOpen` guard this effect would
    // fire confetti + a delayed onClose on every visit by a complete-profile
    // user (and when an edit session ends). The `!isEditing` guard additionally
    // keeps an in-progress edit from being auto-closed; the edit's own
    // `continueToNext` closes the wizard when no unanswered questions remain.
    if (isOpen && !isEditing && profileSteps.length > 0 && completedSteps.length === profileSteps.length) {
      fireConfetti({ particleCount: 100, spread: 70 });
      setTimeout(onClose, 1500);
    }
  }, [isOpen, completedSteps.length, profileSteps.length, onClose, isEditing]);

  const pendingAutoAdvance = useRef(false);

  const clearAutoAdvance = useCallback(() => {
    pendingAutoAdvance.current = false;
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }
  }, []);

  // Auto-advance after selectedValues updates (state is committed)
  useEffect(() => {
    if (!pendingAutoAdvance.current) return;
    if (!currentField || isSaving) return;
    const val = selectedValues[currentField.questionMappingId];
    if (!val) return;

    autoAdvanceTimer.current = setTimeout(() => {
      pendingAutoAdvance.current = false;
      continueToNext();
    }, 400);

    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, [selectedValues, currentField, isSaving, continueToNext]);

  const handleSingleChoiceSelect = useCallback((optionId: string) => {
    clearAutoAdvance();
    pendingAutoAdvance.current = true;
    selectOption(optionId);
  }, [selectOption, clearAutoAdvance]);

  const handleContinueClick = useCallback(() => {
    clearAutoAdvance();
    continueToNext();
  }, [continueToNext, clearAutoAdvance]);

  const handleTextSubmit = useCallback((data: { answer: string }) => {
    setTextValue(data.answer);
    clearAutoAdvance();
    continueToNext();
  }, [setTextValue, continueToNext, clearAutoAdvance]);

  const handleGoBackSafe = useCallback(() => {
    clearAutoAdvance();
    goBack();
  }, [goBack, clearAutoAdvance]);

  const handleCloseSafe = useCallback(async () => {
    clearAutoAdvance();
    // REK-1913: when dismissing an EDIT session (click off / X / "Save and finish
    // later"), commit the current field's pending value so "clicking off accepts
    // the change" holds for text/date/multi-select too (single-choice already
    // auto-saves on selection). Only save a VALID, non-empty value — an invalid
    // or empty text/date edit is discarded rather than persisted. The normal
    // (non-edit) wizard flow is unchanged: dismissing never commits the
    // in-progress field there.
    if (isEditing && currentField) {
      const val = selectedValues[currentField.questionMappingId];
      const hasValue = Array.isArray(val)
        ? val.length > 0
        : typeof val === 'string'
          ? val.trim().length > 0
          : !!val;
      const textOk = !isText || form.formState.isValid;
      const dateOk =
        !isDate ||
        isWithinDateBounds(typeof val === 'string' ? val : '', dateBounds.min, dateBounds.max);
      if (hasValue && textOk && dateOk) {
        const saved = await saveCurrentField();
        // If persistence failed, keep the wizard OPEN rather than closing as if
        // the edit were accepted (which would silently lose the change once the
        // card refetches the prior value). Mirrors continueToNext, which also
        // does not advance/close on a failed save. The user can retry or dismiss
        // again. Nothing-to-save / invalid cases fall through and close normally.
        if (!saved) return;
      }
    }
    onClose();
  }, [
    onClose,
    clearAutoAdvance,
    isEditing,
    currentField,
    selectedValues,
    isText,
    isDate,
    form.formState.isValid,
    dateBounds.min,
    dateBounds.max,
    saveCurrentField,
  ]);

  const handleSkipSafe = useCallback(() => {
    clearAutoAdvance();
    void skipField();
  }, [skipField, clearAutoAdvance]);

  // Wraps the three return paths (error / loading / main) so the shell's
  // open-state animation, focus trap, and dismiss-on-ESC/overlay-click apply
  // uniformly. The shell renders nothing when `open` is false, so the prior
  // `if (!isOpen) return null;` guard becomes redundant.
  const handleOpenChange = (next: boolean) => {
    if (!next) handleCloseSafe();
  };

  if (hasError) {
    return (
      <ResponsiveWizardShell
        open={isOpen}
        onOpenChange={handleOpenChange}
        title="Profile wizard error"
        description="An error occurred while loading the profile questions."
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-4 py-4 border-b border-border">
            <div />
            <Button variant="ghost" size="icon" onClick={handleCloseSafe} className="rounded-lg" aria-label="Close wizard">
              <X className="w-5 h-5 text-muted-foreground" />
            </Button>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center px-6">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-destructive/10 rounded-full">
                  <AlertCircle className="w-12 h-12 text-destructive" />
                </div>
              </div>
              <h2 className="text-2xl font-medium text-foreground mb-2">
                Unable to load profile
              </h2>
              <p className="text-muted-foreground mb-6">
                We couldn't load the profile questions. Please try again later.
              </p>
              <Button onClick={handleCloseSafe} className="rounded-xl">Close</Button>
            </div>
          </div>
        </div>
      </ResponsiveWizardShell>
    );
  }

  if (isLoading) {
    return (
      <ResponsiveWizardShell
        open={isOpen}
        onOpenChange={handleOpenChange}
        title="Loading profile wizard"
      >
        <div className="flex flex-1 items-center justify-center">
          <Spinner className="w-8 h-8 text-primary" />
        </div>
      </ResponsiveWizardShell>
    );
  }

  if (!currentStep || !currentField) {
    // The shell still mounts when there's no current step (race during
    // step transition). Render an empty shell so the open-state animation
    // doesn't flicker.
    return (
      <ResponsiveWizardShell
        open={isOpen}
        onOpenChange={handleOpenChange}
        title="Profile wizard"
      >
        <div className="flex-1" />
      </ResponsiveWizardShell>
    );
  }

  // REK-1869: centralize the Continue gate so we can both disable the button and
  // explain WHY it's disabled. A silently-dead Continue button (e.g. on a
  // question that doesn't apply to the user, so no option is selectable) reads
  // as "broken" — see the Zendesk reports behind REK-1869.
  const dateInRange = isWithinDateBounds(
    typeof currentValue === 'string' ? currentValue : '',
    dateBounds.min,
    dateBounds.max,
  );
  const isContinueDisabled =
    !canContinue ||
    isSaving ||
    (isText && !form.formState.isValid) ||
    (isDate && !dateInRange);
  // Show an actionable hint only when the block is something the user can
  // resolve, and only when the field doesn't already render its own message
  // (date out-of-range message / text <FormMessage />). The common case is a
  // question that doesn't apply -> point the user at Skip.
  const fieldRendersOwnMessage =
    (isDate && typeof currentValue === 'string' && !!currentValue && !dateInRange) ||
    (isText && !!form.formState.errors.answer);
  const continueHint =
    !isSaving && isContinueDisabled && !fieldRendersOwnMessage
      ? 'Choose an answer to continue — or tap "Skip this question" if it doesn\u2019t apply to you.'
      : null;

  return (
    <ResponsiveWizardShell
      open={isOpen}
      onOpenChange={handleOpenChange}
      title={currentStep.title}
      description={currentStep.subtitle}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-border">
        {/* Only a back button (when there's somewhere to go back to). The single
            close affordance is the X in the top-right -- rendering an X here too
            would give the user two close icons on the first question. A spacer
            keeps the progress bar centred when the back button is absent. */}
        {canGoBack ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleGoBackSafe}
            className="rounded-lg"
            aria-label="Go back"
          >
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          </Button>
        ) : (
          <div className="size-10" aria-hidden="true" />
        )}
        
        <div className="flex-1 mx-4">
          <Progress value={completionPercentage} className="h-1.5" />
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground font-medium">
            {completionPercentage}%
          </span>
          <span className="text-xs text-primary font-medium flex items-center gap-1">
            <Gift className="w-3 h-3" />
            {earnedEntries} earned
          </span>
          <Button variant="ghost" size="icon" onClick={handleCloseSafe} className="rounded-lg" aria-label="Close wizard">
            <X className="w-5 h-5 text-muted-foreground" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto min-h-0">
        <div className="max-w-lg mx-auto px-6 py-8 lg:py-12">
          <div className="flex justify-center mb-8">
            <LemonadeAvatar />
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-1.5 mb-6">
            {profileSteps.map((step, idx) => (
              <div
                key={step.id}
                className={cn(
                  "w-2 h-2 rounded-full transition-colors",
                  idx <= currentStepIndex ? 'bg-primary' : 'bg-muted'
                )}
              />
            ))}
          </div>

          {/* Question */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <p className="text-sm font-medium text-primary">
                {currentStep.title}
              </p>
              <span className="text-xs font-bold px-2 py-0.5 bg-primary/20 text-primary rounded-full flex items-center gap-1">
                <Gift className="w-3 h-3" />
                +{currentStep.entriesReward}
              </span>
            </div>
            <h1 className="text-2xl lg:text-3xl font-medium text-foreground mb-2">
              {currentField.question}
            </h1>
            <p className="text-muted-foreground">
              {currentStep.subtitle}
            </p>
          </div>
          {/* Answer area */}
          <div className="space-y-3 mb-8">
            {isMulti && (
              <p className="text-sm text-muted-foreground/80 text-center">
                Select all that apply, then press continue
              </p>
            )}
            {isDate ? (
              (() => {
                const dateValue = typeof currentValue === 'string' ? currentValue : '';
                const outOfRange = !!dateValue && !isWithinDateBounds(dateValue, dateBounds.min, dateBounds.max);
                return (
                  <>
                    <DateField
                      aria-label="Date"
                      value={dateValue}
                      onChange={(v) => setTextValue(v)}
                      min={dateBounds.min}
                      max={dateBounds.max}
                      disabled={isSaving}
                      className="p-4 rounded-xl border-2 border-border/50 bg-card text-base h-auto"
                    />
                    {outOfRange && (
                      <p className="mt-2 text-sm text-destructive">
                        Please enter a valid date of birth — you must be between 18 and 85 years old.
                      </p>
                    )}
                  </>
                );
              })()
            ) : isText ? (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleTextSubmit)}>
                  <FormField
                    control={form.control}
                    name="answer"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            ref={(el) => {
                              field.ref(el);
                              textInputRef.current = el;
                            }}
                            onChange={(e) => {
                              field.onChange(e);
                              setTextValue(e.target.value);
                            }}
                            placeholder={inputHint?.placeholder || currentField.placeholder || 'Enter your answer'}
                            inputMode={inputHint?.inputMode}
                            autoComplete={inputHint?.autoComplete}
                            maxLength={inputHint?.maxLength}
                            autoFocus
                            disabled={isSaving}
                            className="w-full p-4 rounded-xl border-2! border-border/50! bg-card text-base focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-primary! transition-all h-auto"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* Hidden submit for Enter key on short inputs. See the
                   * matching comment in CampaignResponseWizard.tsx for the
                   * full rationale; swapped to shadcn Button per Phase 6a R3
                   * (REK-1544). Variant=ghost + sr-only + tabIndex=-1 +
                   * aria-hidden is a no-op visually + a11y but preserves the
                   * HTML form-submit-on-Enter semantics. */}
                  <Button
                    type="submit"
                    variant="ghost"
                    tabIndex={-1}
                    aria-hidden="true"
                    className="sr-only"
                  />
                </form>
              </Form>
            ) : isMulti ? (
              currentField.options?.map((option) => {
                const selectedOptions = (Array.isArray(currentValue) ? currentValue : []) as string[];
                const isSelected = selectedOptions.includes(option.id);

                return (
                  <Label
                    key={option.id}
                    htmlFor={`profile-opt-${option.id}`}
                    className={cn(
                      "flex items-center gap-3 w-full p-4 rounded-xl border-2 text-left transition-all cursor-pointer",
                      "hover:border-primary/50 hover:bg-muted/30",
                      isSelected
                        ? "border-primary bg-primary/10 shadow-lg"
                        : "border-border/50 bg-card"
                    )}
                  >
                    <Checkbox
                      id={`profile-opt-${option.id}`}
                      checked={isSelected}
                      disabled={isSaving}
                      onCheckedChange={(checked) => {
                        selectOption(option.id);
                      }}
                    />
                    <span className="text-base font-medium text-foreground">{option.label}</span>
                  </Label>
                );
              })
            ) : (
              <RadioGroup
                value={typeof currentValue === 'string' ? currentValue : ''}
                onValueChange={(value) => handleSingleChoiceSelect(value)}
                disabled={isSaving}
              >
                {currentField.options?.map((option) => {
                  const isSelected = currentValue === option.id;
                  return (
                    <Label
                      key={option.id}
                      htmlFor={`profile-opt-${option.id}`}
                      className={cn(
                        "flex items-center gap-3 w-full p-4 rounded-xl border-2 text-left transition-all cursor-pointer",
                        "hover:border-primary/50 hover:bg-muted/30",
                        isSelected
                          ? "border-primary bg-primary/10 shadow-lg"
                          : "border-border/50 bg-card"
                      )}
                    >
                      <RadioGroupItem value={option.id} id={`profile-opt-${option.id}`} />
                      <span className="text-base font-medium text-foreground">{option.label}</span>
                    </Label>
                  );
                })}
              </RadioGroup>
            )}
          </div>

          {/* Continue Button */}
          <Button
            onClick={isText ? () => form.handleSubmit(handleTextSubmit)() : handleContinueClick}
            disabled={isContinueDisabled}
            className="w-full py-6 text-lg rounded-xl font-medium"
          >
            {isSaving ? (
              <>
                <Spinner className="mr-2" />
                Saving...
              </>
            ) : (
              'Continue'
            )}
          </Button>

          {/* REK-1869: explain a disabled Continue so it doesn't look broken. */}
          {continueHint && (
            <p
              className="mt-3 text-center text-sm text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              {continueHint}
            </p>
          )}

          <Button
            variant="ghost"
            onClick={handleSkipSafe}
            disabled={isSaving}
            className="w-full mt-3 text-muted-foreground text-sm"
          >
            Skip this question
          </Button>

          <Button
            variant="ghost"
            onClick={handleCloseSafe}
            className="w-full mt-3 text-muted-foreground text-sm underline underline-offset-2"
          >
            Save and finish later
          </Button>
        </div>
      </div>
    </ResponsiveWizardShell>
  );
}
